import { Controller } from '@hotwired/stimulus';

/**
 * Tooltip Controller — infobulle au survol et au focus, portée dans un portail sous `<body>`
 * (position `fixed`) pour échapper aux ancêtres `overflow-hidden`.
 *
 * Refonte de l'espace chantier V2 (G-18, WCAG 1.4.13), rétrocompatible pour tous les montages
 * (`Ui::TooltipComponent` et montages manuels) :
 * - `delay` (ms, défaut 0) : attente au SURVOL seulement ; au focus, l'infobulle est immédiate ;
 * - Échap masque l'infobulle affichée, sans `preventDefault` ni `stopPropagation` par défaut : le
 *   dialogue ou le menu qui l'entoure reçoit aussi sa touche et se ferme du même coup ;
 * - opt-in `escapeCaptures` (refonte de la modale « Diffusion du CR ») : tant que l'infobulle est
 *   affichée, Échap est écouté en phase de CAPTURE sur `document`, la masque et arrête la
 *   propagation, qu'elle soit montrée au focus ou au seul survol (WCAG 1.4.13 : le contenu survolé
 *   se ferme sans fermer son contexte) : la modale autour (écouteur `keydown` de bouillonnement) ne
 *   se ferme qu'au second Échap. Seule exception, le focus dans un menu ouvert (`[role="menu"]`, le
 *   portail du menu ⋯) : l'infobulle se masque et laisse Échap atteindre le menu, qui se ferme.
 *   L'écouteur est retiré à chaque masquage (perte de focus, Échap, déconnexion) ;
 * - au départ du pointeur, un court délai de grâce (`HIDE_GRACE_MS`) laisse le temps de passer
 *   sur l'infobulle, qui reste affichée tant que le pointeur la survole ;
 * - une seule infobulle affichée à la fois ;
 * - rien ne s'affiche sous `[data-tooltips="off"]` (menu latéral déplié,
 *   `sidebar_collapse_controller.js`) ;
 * - le contentTarget garde son `role="tooltip"`, et son id quand le serveur en rend un (seulement
 *   avec `describe:`, lu par `aria-describedby`) ; le portail reprend le rôle, avec un id DÉRIVÉ
 *   (`<id>-portal`) s'il y en a un, et ses copies perdent leurs ids : aucun id en double.
 *
 * Targets:
 * - trigger: l'élément survolé (facultatif : le montage manuel place les actions sur l'hôte)
 * - content: le contenu caché recopié dans le portail
 *
 * Values:
 * - placement (String): top (défaut), bottom, left, right
 * - delay (Number): délai d'affichage au survol, en ms (défaut 0)
 * - escapeCaptures (Boolean): Échap pris en capture et arrêté quand l'infobulle est affichée, sauf
 *   focus dans un menu ouvert (défaut false : comportement historique)
 *
 * Actions:
 * - mouseenter->tooltip#show mouseleave->tooltip#hide focusin->tooltip#show focusout->tooltip#hide
 *
 * @example
 * <a href="/chantiers" data-controller="tooltip" data-tooltip-placement-value="right"
 *    data-tooltip-delay-value="300"
 *    data-action="mouseenter->tooltip#show mouseleave->tooltip#hide focusin->tooltip#show focusout->tooltip#hide">
 *   …
 *   <span id="tooltip-1a2b3c4d" role="tooltip" data-tooltip-target="content" class="hidden">Chantiers</span>
 * </a>
 */

export const HIDE_GRACE_MS = 150;

const POINTER_ENTER_TYPES = new Set(['mouseenter', 'mouseover', 'pointerenter', 'pointerover']);
const POINTER_LEAVE_TYPES = new Set(['mouseleave', 'mouseout', 'pointerleave', 'pointerout']);

/**
 * Délai avant affichage (pur) : la valeur `delay` au survol, 0 au focus (et pour tout
 * autre déclencheur).
 * @param {string|undefined} eventType
 * @param {number} delay
 * @returns {number}
 */
export function showDelay(eventType, delay) {
  return POINTER_ENTER_TYPES.has(eventType) && delay > 0 ? delay : 0;
}

/**
 * Le départ vient-il du pointeur (pur) ? Lui seul a droit au délai de grâce ; une perte de
 * focus masque tout de suite.
 * @param {string|undefined} eventType
 * @returns {boolean}
 */
export function isPointerLeave(eventType) {
  return POINTER_LEAVE_TYPES.has(eventType);
}

/**
 * Infobulles coupées par un ancêtre (pur) : `[data-tooltips="off"]`.
 * @param {Element} element
 * @returns {boolean}
 */
export function tooltipsSuppressed(element) {
  return Boolean(element?.closest?.('[data-tooltips="off"]'));
}

/**
 * Id du portail (pur) : dérivé de celui du contentTarget, jamais le même.
 * @param {string} contentId
 * @returns {string}
 */
export function portalId(contentId) {
  return contentId ? `${contentId}-portal` : '';
}

/**
 * Le focus est-il dans un menu ouvert (`[role="menu"]`, le portail du menu ⋯) ? Un menu fermé est
 * masqué : rien n'y a le focus.
 * @returns {boolean}
 */
function focusInOpenMenu() {
  return Boolean(document.activeElement?.closest?.('[role="menu"]'));
}

/**
 * Un menu ⋯ est-il ouvert, focus dedans ou non ? Son portail (`data-dropdown-portal`) n'existe que
 * menu ouvert : `dropdown_controller.js#createPortal` le pose, `close()` le retire. Un clic dans le
 * menu hors de ses items sort le focus du menu et le laisse ouvert.
 * @returns {boolean}
 */
function menuOpen() {
  return (
    focusInOpenMenu() || Boolean(document.querySelector('[data-dropdown-portal] [role="menu"]'))
  );
}

function stripIds(node) {
  if (node.nodeType !== 1) return node;
  node.removeAttribute('id');
  Array.from(node.children).forEach(stripIds);
  return node;
}

// Les contrôleurs dont l'infobulle est affichée (un au plus) : une seule à la fois.
const shownControllers = new Set();

export default class extends Controller {
  static targets = ['trigger', 'content'];
  static values = {
    placement: { type: String, default: 'top' }, // top, bottom, left, right
    delay: { type: Number, default: 0 },
    escapeCaptures: { type: Boolean, default: false },
  };

  connect() {
    this.tooltipElement = null;
    this.showTimer = null;
    this.hideTimer = null;
    // Phase de l'écouteur Échap posé à l'affichage, figée jusqu'au masquage (relue par handleKeydown).
    this.capturesEscape = false;
    this.onKeydown = this.handleKeydown.bind(this);
  }

  disconnect() {
    this.hideNow();
    this.hideTooltip();
  }

  // Affiche le tooltip au survol (après `delay`) ou au focus (tout de suite)
  show(event) {
    // `currentTarget` n'existe plus quand le délai expire : on le lit maintenant.
    const trigger = event?.currentTarget ?? this.element;
    this.clearHideTimer();
    this.clearShowTimer();

    if (tooltipsSuppressed(this.element)) {
      this.hideNow();
      return;
    }

    const wait = this.isVisible ? 0 : showDelay(event?.type, this.delayValue);
    if (wait > 0) {
      this.showTimer = setTimeout(() => {
        this.showTimer = null;
        this.display(trigger);
      }, wait);
      return;
    }
    this.display(trigger);
  }

  // Cache le tooltip : après un délai de grâce si le pointeur part, tout de suite sinon
  hide(event) {
    this.clearShowTimer();
    if (!this.isVisible) return;

    if (isPointerLeave(event?.type)) {
      this.scheduleHide();
    } else {
      this.hideNow();
    }
  }

  get isVisible() {
    return Boolean(this.tooltipElement && !this.tooltipElement.classList.contains('hidden'));
  }

  display(trigger) {
    if (tooltipsSuppressed(this.element)) return;
    shownControllers.forEach((other) => {
      if (other !== this) other.hideNow();
    });

    // Créer le tooltip s'il n'existe pas
    if (!this.tooltipElement) {
      this.createTooltip();
    }

    // Rendre visible mais invisible pour mesurer les dimensions
    this.tooltipElement.style.visibility = 'hidden';
    this.tooltipElement.classList.remove('hidden');

    // Positionner le tooltip (maintenant les dimensions sont mesurables)
    this.positionTooltip(trigger);

    // Afficher le tooltip
    this.tooltipElement.style.visibility = '';

    shownControllers.add(this);
    this.addKeydownListener();
  }

  hideNow() {
    this.clearShowTimer();
    this.clearHideTimer();
    if (this.tooltipElement) {
      this.tooltipElement.classList.add('hidden');
    }
    this.removeKeydownListener();
    shownControllers.delete(this);
  }

  // Sans opt-in : bouillonnement, comme avant. Avec `escapeCaptures` : capture, pour passer avant
  // l'écouteur de la modale hôte, inscrit plus tôt sur `document`.
  addKeydownListener() {
    if (this.capturesEscape !== Boolean(this.escapeCapturesValue)) this.removeKeydownListener();
    this.capturesEscape = Boolean(this.escapeCapturesValue);
    if (this.capturesEscape) {
      document.addEventListener('keydown', this.onKeydown, { capture: true });
    } else {
      document.addEventListener('keydown', this.onKeydown);
    }
  }

  // Les deux phases : un retrait sans `{ capture: true }` laisserait fuir l'écouteur de capture.
  removeKeydownListener() {
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('keydown', this.onKeydown, { capture: true });
  }

  scheduleHide() {
    this.clearHideTimer();
    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      this.hideNow();
    }, HIDE_GRACE_MS);
  }

  clearShowTimer() {
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
  }

  clearHideTimer() {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }

  handleKeydown(event) {
    if (event.key !== 'Escape') return;
    // Lu avant `hideNow`, qui retire l'écouteur. Jamais de `preventDefault`. Arrêtée dès que
    // l'infobulle est affichée (survol ou focus, WCAG 1.4.13), sauf menu ouvert (focus dedans ou non) :
    // la touche continue vers lui, ou vers la modale comme sans infobulle.
    const stops = this.capturesEscape && this.isVisible && !menuOpen();
    this.hideNow();
    if (stops) event.stopPropagation();
  }

  // Crée l'élément tooltip dans le body : copies des nœuds du contenu (jamais `innerHTML`),
  // sans leurs ids ; le rôle et un id dérivé passent sur le portail.
  createTooltip() {
    const content = this.contentTarget;
    this.tooltipElement = document.createElement('div');
    this.tooltipElement.className = 'fixed z-50 hidden';

    const role = content.getAttribute('role');
    if (role) this.tooltipElement.setAttribute('role', role);
    if (content.id) this.tooltipElement.id = portalId(content.id);

    Array.from(content.childNodes).forEach((node) => {
      this.tooltipElement.appendChild(stripIds(node.cloneNode(true)));
    });

    // WCAG 1.4.13 : l'infobulle survolée reste affichée.
    this.tooltipElement.addEventListener('mouseenter', () => this.clearHideTimer());
    this.tooltipElement.addEventListener('mouseleave', () => this.scheduleHide());

    document.body.appendChild(this.tooltipElement);
  }

  // Positionne le tooltip par rapport au trigger
  positionTooltip(triggerElement) {
    const triggerRect = triggerElement.getBoundingClientRect();
    const tooltipRect = this.tooltipElement.getBoundingClientRect();

    let top, left;

    switch (this.placementValue) {
      case 'top':
        // Au-dessus, centré horizontalement avec un gap de 8px
        top = triggerRect.top - tooltipRect.height - 8;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        break;

      case 'bottom':
        // Centré en-dessous avec un gap de 8px
        top = triggerRect.bottom + 8;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        break;

      case 'left':
        // À gauche, centré verticalement
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.left - tooltipRect.width - 8;
        break;

      case 'right':
        // À droite, centré verticalement
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.right + 8;
        break;
    }

    // S'assurer que le tooltip reste dans la fenêtre
    // Ajustement horizontal
    if (left < 8) {
      left = 8;
    } else if (left + tooltipRect.width > window.innerWidth - 8) {
      left = window.innerWidth - tooltipRect.width - 8;
    }

    // Ajustement vertical
    if (top < 8) {
      top = 8;
    } else if (top + tooltipRect.height > window.innerHeight - 8) {
      top = window.innerHeight - tooltipRect.height - 8;
    }

    this.tooltipElement.style.top = `${top}px`;
    this.tooltipElement.style.left = `${left}px`;
  }

  // Nettoie le tooltip à la déconnexion
  hideTooltip() {
    if (this.tooltipElement) {
      this.tooltipElement.remove();
      this.tooltipElement = null;
    }
  }
}
